import { useState, useEffect, useRef } from "react";

const PARTICIPANTS = [
  { name: "Sarah M.", entries: 8, avatar: "SM", color: "#0d9488" },
  { name: "Jake T.", entries: 3, avatar: "JT", color: "#f97316" },
  { name: "Priya K.", entries: 12, avatar: "PK", color: "#6366f1" },
  { name: "Carlos R.", entries: 5, avatar: "CR", color: "#ec4899" },
];

const WINNER = PARTICIPANTS[2];

export default function WinnerSelection(): JSX.Element {
  const [highlighted, setHighlighted] = useState<number | null>(null);
  const [winner, setWinner] = useState<typeof PARTICIPANTS[0] | null>(null);
  const [spinning, setSpinning] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const runSelection = () => {
    setWinner(null);
    setSpinning(true);
    let idx = 0;
    let count = 0;
    const maxSpins = 18;

    const spin = () => {
      setHighlighted(idx % PARTICIPANTS.length);
      idx++;
      count++;
      const delay = count < 10 ? 80 : count < 15 ? 140 : 220;
      if (count < maxSpins) {
        const t = setTimeout(spin, delay);
        timeoutsRef.current.push(t);
      } else {
        setHighlighted(PARTICIPANTS.indexOf(WINNER));
        setSpinning(false);
        const t = setTimeout(() => setWinner(WINNER), 300);
        timeoutsRef.current.push(t);
        // Reset after pause
        const t2 = setTimeout(() => {
          setWinner(null);
          setHighlighted(null);
        }, 4000);
        timeoutsRef.current.push(t2);
      }
    };
    spin();
  };

  useEffect(() => {
    const t = setTimeout(runSelection, 800);
    const loop = setInterval(runSelection, 6000);
    return () => {
      clearTimeout(t);
      clearInterval(loop);
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  return (
    <div style={{
      width: "100%", maxWidth: 400, height: "auto", minHeight: 200, aspectRatio: "3/2", overflow: "hidden",
      background: "#fff", borderRadius: 14,
      border: "1px solid #e2e8f0",
      boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
      fontFamily: "'Inter', -apple-system, sans-serif",
      display: "flex", flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{
        background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
        padding: "10px 14px", display: "flex",
        alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
          🏆 Pick a Winner
        </span>
        <span style={{ fontSize: 10, color: "#94a3b8" }}>
          {PARTICIPANTS.reduce((a, b) => a + b.entries, 0)} total entries
        </span>
      </div>

      {/* Participant grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr",
        gap: 8, padding: "10px 14px", flex: 1,
      }}>
        {PARTICIPANTS.map((p, i) => {
          const isHighlighted = highlighted === i;
          const isWinner = winner?.name === p.name;
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 12px",
              background: isWinner
                ? `${p.color}12`
                : isHighlighted ? "#f8fafc" : "#fafafa",
              border: `1px solid ${isWinner ? p.color
                : isHighlighted ? "#e2e8f0" : "#f1f5f9"}`,
              borderRadius: 8,
              transition: "all 0.12s ease",
              opacity: winner && !isWinner ? 0.35 : 1,
              boxShadow: isWinner ? `0 0 0 2px ${p.color}30` : "none",
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: p.color, flexShrink: 0,
                display: "flex", alignItems: "center",
                justifyContent: "center",
                fontSize: 9, fontWeight: 800, color: "#fff",
              }}>
                {isWinner ? "🏆" : p.avatar}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 700,
                  color: "#0f172a",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>{p.name}</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>
                  {p.entries} {p.entries === 1 ? "entry" : "entries"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        padding: "0 14px 12px",
        flexShrink: 0,
      }}>
        {winner ? (
          <div style={{
            background: `${winner.color}10`,
            border: `1px solid ${winner.color}40`,
            borderRadius: 8, padding: "8px 12px",
            display: "flex", alignItems: "center",
            justifyContent: "center", gap: 8,
            animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)",
          }}>
            <span style={{ fontSize: 14 }}>🎉</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
              {winner.name} wins!
            </span>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>
              Selected from {winner.entries} entries
            </span>
          </div>
        ) : (
          <div style={{
            background: spinning
              ? "rgba(249,115,22,0.08)"
              : "linear-gradient(135deg, #f97316, #ea580c)",
            border: spinning ? "1px solid rgba(249,115,22,0.2)" : "none",
            borderRadius: 8, padding: "9px",
            textAlign: "center", fontSize: 11,
            fontWeight: 700,
            color: spinning ? "#f97316" : "#fff",
            transition: "all 0.3s",
            boxShadow: spinning ? "none" : "0 3px 12px rgba(249,115,22,0.3)",
          }}>
            {spinning ? "Selecting winner..." : "🎯 Pick a Winner"}
          </div>
        )}
      </div>

      <style>{`
        @keyframes popIn {
          from { opacity:0; transform:scale(0.94); }
          to { opacity:1; transform:scale(1); }
        }
      `}</style>
    </div>
  );
}
